import { useCallback, useEffect, useState, type JSX } from "react";
import { Controller, FormProvider, SubmitHandler, useForm } from "react-hook-form";
import { useNavigate } from "react-router";

import DocumentationIconButton from "components/DocumentationIconButton";
import PageLoading from "components/loading/PageLoading";
import Box from "ds/components/Box";
import Button from "ds/components/Button";
import FormField from "ds/components/Form/Field";
import FullScreenModal from "ds/components/FullScreenModal";
import FullScreenModalBody from "ds/components/FullScreenModal/Body";
import FullScreenModalFooter from "ds/components/FullScreenModal/Footer";
import Input from "ds/components/Input";
import { showSimpleLeaveConfirmation } from "ds/components/LeaveConfirmationModal/Simple";
import Textarea from "ds/components/Textarea";
import TooltipModalBody from "ds/components/TooltipModal/Body";
import { TooltipModalTitle } from "ds/components/TooltipModal/Title";
import { AnalyticsPageOrganization } from "hooks/useAnalytics/pages/organization";
import { getDocsUrl } from "utils/getDocsUrl";

import RoleFullScreenFormSection from "./Section";
import { getTooltipAnalyticsProps, validateRequired } from "./utils";
import { FormValues } from "./types";
import { useRoleFormConfig, useDefaultFormValues } from "./hooks";
import styles from "./styles.module.css";

type RoleFullScreenFormProps = {
  title: string;
  leaveTitle: string;
  analyticsPage: AnalyticsPageOrganization;
  saveButtonTitle: string;
  isFormDataLoading?: boolean;
  submitLoading?: boolean;
  onSubmit: SubmitHandler<FormValues>;
  backUrl: string;
  initialValues?: FormValues;
  withDetails?: boolean;
};

const RoleFullScreenForm = ({
  title,
  analyticsPage,
  saveButtonTitle,
  isFormDataLoading,
  leaveTitle,
  submitLoading,
  onSubmit,
  initialValues,
  backUrl,
  withDetails = true,
}: RoleFullScreenFormProps) => {
  const navigate = useNavigate();
  const config = useRoleFormConfig();
  const defaultValues = useDefaultFormValues();

  const [AccountOrAuthErrorContent] = useState<JSX.Element | undefined>();

  const form = useForm({
    defaultValues: initialValues || defaultValues,
    mode: "onChange",
  });

  const {
    control,
    register,
    handleSubmit,
    setValue,
    formState: { isDirty, errors },
  } = form;

  const modalCloseClickHandler = useCallback(async () => {
    if (isDirty) {
      await showSimpleLeaveConfirmation({
        title: leaveTitle,
        message: "Your changes will not be saved.",
      });
    }

    navigate(backUrl, { replace: true });
  }, [isDirty, backUrl, navigate, leaveTitle]);

  // TODO: async name validation
  // const handleNameChange = useCallback(
  //   (event: React.ChangeEvent<HTMLInputElement>) => {
  //     const value = event.target.value;

  //     // if (value.length > 0) {
  //     //   validateName(value);
  //     // }

  //     setValue("name", value);
  //     trigger("name");
  //   },
  //   [setValue, trigger]
  // );

  useEffect(() => {
    if (initialValues) {
      setValue("name", initialValues.name);
      setValue("description", initialValues.description);
      setValue("permissions", initialValues.permissions);
    }
  }, [initialValues, setValue]);

  const handleFormSubmit = useCallback(() => {
    handleSubmit(onSubmit)();
  }, [onSubmit, handleSubmit]);

  if (AccountOrAuthErrorContent) {
    return AccountOrAuthErrorContent;
  }

  return (
    <FullScreenModal title={title} onClose={modalCloseClickHandler}>
      <FormProvider {...form}>
        <Box grow="1" fullWidth scrollable>
          <Box fullWidth direction="column" relative>
            {isFormDataLoading ? (
              <PageLoading />
            ) : (
              <FullScreenModalBody>
                {withDetails && (
                  <>
                    <Controller
                      name="name"
                      control={control}
                      rules={{ validate: validateRequired("Name") }}
                      render={({ field, fieldState }) => (
                        <FormField
                          label="Name"
                          error={errors?.name?.message}
                          tooltipInfoVariant="modal"
                          {...getTooltipAnalyticsProps("Details", "Name", analyticsPage)}
                          tooltipInfo={
                            <>
                              <TooltipModalTitle>Name your role</TooltipModalTitle>
                              <TooltipModalBody align="start">
                                Name of this role. It should be unique within one account
                              </TooltipModalBody>
                            </>
                          }
                        >
                          {({ ariaInputProps }) => (
                            <Input
                              placeholder="Name your role"
                              ref={field.ref}
                              error={!!fieldState.error}
                              type="text"
                              value={field.value}
                              onChange={field.onChange}
                              name="name"
                              {...ariaInputProps}
                            />
                          )}
                        </FormField>
                      )}
                    />
                    <FormField
                      label="Description"
                      isOptional
                      tooltipInfoVariant="modal"
                      {...getTooltipAnalyticsProps("Details", "Description", analyticsPage)}
                      tooltipInfo={
                        <>
                          <TooltipModalTitle>Describe your role</TooltipModalTitle>
                          <TooltipModalBody align="start">
                            Human-friendly description of this role - supports Markdown. This value
                            is only used to make life easier for you, the user. You can use this
                            space to describe your role, provide useful links, or embed a cat GIF.
                            The only limit is yourself.
                          </TooltipModalBody>
                        </>
                      }
                    >
                      {({ ariaInputProps }) => (
                        <Textarea
                          placeholder="Enter role description here..."
                          {...register("description")}
                          {...ariaInputProps}
                        />
                      )}
                    </FormField>
                  </>
                )}
                <Box direction="column" padding={withDetails ? "x-large 0 0 0" : "0"}>
                  <Box
                    className={styles.sections}
                    gap="large"
                    direction="column"
                    padding={withDetails ? "x-large 0 0 0" : "0"}
                  >
                    {config.map((section, i) => (
                      <RoleFullScreenFormSection key={i} {...section} />
                    ))}
                  </Box>
                </Box>
              </FullScreenModalBody>
            )}
            <FullScreenModalFooter justify="between">
              <Box gap="medium">
                <DocumentationIconButton
                  // TODO: update button link
                  href={getDocsUrl("/")}
                  tooltipText="Open documentation"
                  analyticsPage={analyticsPage}
                  analyticsTitle="Docs button click"
                />
              </Box>
              <Box direction="row" gap="medium">
                <Button
                  disabled={isFormDataLoading}
                  variant="secondary"
                  onPress={modalCloseClickHandler}
                >
                  Cancel
                </Button>

                <Button
                  variant="primary"
                  onPress={handleFormSubmit}
                  loading={submitLoading}
                  disabled={isFormDataLoading}
                >
                  {saveButtonTitle}
                </Button>
              </Box>
            </FullScreenModalFooter>
          </Box>
        </Box>
      </FormProvider>
    </FullScreenModal>
  );
};

export default RoleFullScreenForm;
